import React from 'react'

import {
  DocumentHead,
  DocumentHeader,
  ErrorMessage,
  handleFetchMore,
  LoadingState,
  useForTablet,
  useModalContext,
} from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'

import {
  WorkspaceMinimalFragment,
  useGetProjectsListQuery,
} from '@sketch/gql-types'

import ShareEmptyState from '../ShareEmptyState'

import ProjectItemsLayout from '../ProjectItemsLayout'
import ProjectDropdown from '../../../projects/components/ProjectDropdown'
import { RenameProject } from '../../../projects'
import {
  useSearch,
  useOnEvent,
  LayoutPortal,
  workspaceSidebarLayoutSymbols,
} from '@sketch/utils'
import { useFlag, useFolderTerm } from '@sketch/modules-common'

const ENTRIES_PATH = ['workspace', 'archived', 'entries']

export interface ArchiveListOldProps {
  workspaceId: string
  workspace: WorkspaceMinimalFragment
}

export function ArchiveListOld({
  workspaceId,
  workspace,
}: ArchiveListOldProps) {
  const { searchDebounced: search } = useSearch()
  const isTabletAndBigger = useForTablet()

  const { showModal } = useModalContext()

  const { loading, error, data, fetchMore, refetch } = useGetProjectsListQuery({
    variables: {
      topLevel: !useFlag('nested-projects'),
      workspaceId,
      archived: 'ARCHIVED',
      searchTerm: search,
    },
  })

  useOnEvent('workspaceShareRefresh', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId) {
      // Not the workspace visible ignore then
      return
    }

    refetch()
  })

  const folderTerm = useFolderTerm()

  if (loading) {
    return <LoadingState />
  }

  if (!data || error) {
    return <ErrorMessage.Generic />
  }

  const totalCount = data?.workspace.projects.meta.totalCount || 0
  const documentHeader = isTabletAndBigger && (
    <>
      <DocumentHead title="Archive" />
      <LayoutPortal token={workspaceSidebarLayoutSymbols.header}>
        <DocumentHeader
          title={`Archived ${folderTerm.upper}s`}
          description={`Use the Archive to store ${folderTerm.lower}s that you no longer need to access frequently.`}
          learnMoreTooltipTitle="Learn more about archive"
          learnMoreTooltipURL="https://www.sketch.com/redirect/web/archive-projects"
        />
      </LayoutPortal>
      <LayoutPortal token={workspaceSidebarLayoutSymbols.headerSlim}>
        <DocumentHeader title={`Archived ${folderTerm.upper}s`} />
      </LayoutPortal>
    </>
  )

  if (!loading && totalCount === 0) {
    return (
      <>
        {documentHeader}

        <ShareEmptyState
          search={search}
          workspace={workspace}
          filter="ARCHIVE_PROJECTS"
        />
      </>
    )
  }

  const { after } = data.workspace.projects.meta
  const loadMoreHandler = handleFetchMore(fetchMore, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  const items = data.workspace.projects.entries

  return (
    <>
      {documentHeader}

      <ProjectItemsLayout
        projects={items}
        onLoadMore={loadMoreHandler}
        totalCount={totalCount}
        renderDropdown={project => (
          <ProjectDropdown
            workspace={workspace}
            project={project}
            onRename={() => {
              showModal(RenameProject, {
                workspaceId,
                projectId: project.identifier,
                projectName: project.name,
              })
            }}
          />
        )}
        workspaceId={workspaceId}
      />
    </>
  )
}
